export default function HeroSection() {
  return (
    <section className="relative w-full min-h-[620px] md:min-h-[700px] flex items-center overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-background-100 via-background-50 to-accent-50/60"></div>

      {/* Subtle decorative elements */}
      <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-primary-100/40 blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-accent-100/30 blur-3xl"></div>

      <div className="relative w-full px-4 md:px-6 lg:px-10 pt-20 pb-12">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Left Content */}
          <div className="w-full lg:w-[60%] text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50 border border-background-200 mb-6">
              <div className="w-2 h-2 rounded-full bg-accent-500 animate-pulse"></div>
              <span className="text-xs font-medium text-foreground-600 whitespace-nowrap">
                Updated Resources &amp; Organized Categories
              </span>
            </div>

            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-foreground-950 leading-[1.08] mb-5">
              <span className="block">Kakobuy Spreadsheet</span>
              <span className="block text-primary-500">The Ultimate Database</span>
              <span className="block">For Product Discovery</span>
            </h1>

            <p className="text-base md:text-lg text-foreground-600 max-w-lg mx-auto lg:mx-0 mb-8 leading-relaxed">
              Discover thousands of organized product finds, category spreadsheets, buying guides, and comparison resources in one powerful platform. Your shortcut to smarter shopping.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center gap-3 justify-center lg:justify-start">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto text-center px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                kakobuy sheet
                <i className="ri-arrow-right-up-line ml-1.5"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto text-center px-8 py-3.5 rounded-full border-2 border-foreground-300 text-foreground-800 text-sm font-semibold hover:border-foreground-500 hover:text-foreground-950 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                buy it now
              </a>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-8 justify-center lg:justify-start">
              {[
                { icon: 'ri-checkbox-circle-line', text: 'Updated Resources' },
                { icon: 'ri-stack-line', text: 'Organized Categories' },
                { icon: 'ri-user-star-line', text: 'Beginner Friendly' },
                { icon: 'ri-shield-check-line', text: 'Safety Information' },
              ].map((badge) => (
                <div key={badge.text} className="flex items-center gap-1.5 text-foreground-500">
                  <i className={`${badge.icon} text-accent-500 text-sm`}></i>
                  <span className="text-xs font-medium whitespace-nowrap">{badge.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right side - Feature highlight cards */}
          <div className="w-full lg:w-[40%]">
            <div className="grid grid-cols-1 gap-4">
              {/* Card 1 */}
              <div className="bg-background-50/80 backdrop-blur-sm rounded-2xl p-5 border border-background-200/60">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-search-eye-line text-lg"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-base mb-1">
                  Fast Product Discovery
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Browse thousands of organized finds across multiple categories without endless manual searching.
                </p>
              </div>

              {/* Card 2 */}
              <div className="bg-background-50/80 backdrop-blur-sm rounded-2xl p-5 border border-background-200/60">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 mb-3">
                  <i className="ri-arrow-left-right-line text-lg"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-base mb-1">
                  Cross-Platform Comparison
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Compare products from Kakobuy, CSSBuy, Superbuy, and Oopbuy side by side in one unified database.
                </p>
              </div>

              {/* Card 3 */}
              <div className="bg-background-50/80 backdrop-blur-sm rounded-2xl p-5 border border-background-200/60">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-guide-line text-lg"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-base mb-1">
                  Step-by-Step Buying Guides
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Learn exactly how to navigate the shopping process with detailed tutorials crafted for beginners.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom image strip */}
        <div className="mt-12 md:mt-16">
          <div className="relative w-full h-48 md:h-56 rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Artful%20flat%20lay%20of%20diverse%20fashion%20items%20including%20leather%20sneakers%2C%20a%20folded%20linen%20shirt%2C%20sunglasses%2C%20a%20canvas%20bag%2C%20and%20a%20watch%20scattered%20organically%20on%20a%20warm%20terracotta%20toned%20surface%2C%20soft%20diffused%20natural%20light%2C%20editorial%20lifestyle%20photography%2C%20harmonious%20color%20palette%2C%20elegant%20composition&width=1440&height=400&seq=hero-collage-v3-2026&orientation=landscape"
              alt="Kakobuy Spreadsheet product finds collection - shoes, hoodies, and accessories"
              title="Kakobuy Spreadsheet Product Collection"
              className="w-full h-full object-cover object-top"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50/30 to-transparent"></div>
          </div>
        </div>
      </div>
    </section>
  );
}